import { CalendarDays, CreditCard } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import type { PayLaterOption } from "@/lib/api/types"

interface PayLaterInfoModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  payLaterOption: PayLaterOption
  currency: string
}

import { formatMoney } from "@/lib/booking"

export function PayLaterInfoModal({
  open,
  onOpenChange,
  payLaterOption,
  currency,
}: PayLaterInfoModalProps) {
  const remainingPayments = payLaterOption.installments - 1
  const deposit = formatMoney(payLaterOption.depositAmount, currency)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md rounded-2xl border-border bg-card p-0"
      >
        <div className="p-6 pb-0">
          <DialogHeader>
            <div
              className="mb-3 flex size-11 items-center justify-center rounded-xl"
              style={{ background: "color-mix(in oklch, var(--primary) 12%, transparent)" }}
            >
              <CalendarDays className="size-5 text-primary" />
            </div>
            <DialogTitle className="text-lg font-bold tracking-wide text-foreground uppercase">
              Split Payment
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Book your trip now and pay the rest later. No extra fees, just
              more flexibility.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="space-y-4 p-6">
          <div className="rounded-xl border border-border p-4">
            <div className="mb-3 flex items-center gap-2.5">
              <CreditCard className="size-4 text-primary" />
              <span className="text-sm font-bold text-foreground">
                How it works
              </span>
            </div>
            <ul className="space-y-2.5">
              <li className="flex items-start gap-2.5">
                <span className="mt-1.5 block size-1.5 shrink-0 rounded-full bg-primary" />
                <span className="text-sm text-foreground/85">
                  Pay only <strong>{deposit}</strong> deposit today to secure
                  your booking
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="mt-1.5 block size-1.5 shrink-0 rounded-full bg-primary" />
                <span className="text-sm text-foreground/85">
                  Remaining balance paid in{" "}
                  <strong>
                    {remainingPayments} payment
                    {remainingPayments !== 1 ? "s" : ""}
                  </strong>
                </span>
              </li>
              {payLaterOption.installments === 3 && (
                <li className="flex items-start gap-2.5">
                  <span className="mt-1.5 block size-1.5 shrink-0 rounded-full bg-primary" />
                  <span className="text-sm text-foreground/85">
                    Each payment equals exactly one third of the total
                  </span>
                </li>
              )}
              <li className="flex items-start gap-2.5">
                <span className="mt-1.5 block size-1.5 shrink-0 rounded-full bg-primary" />
                <span className="text-sm text-foreground/85">
                  Automatic reminders before each due date
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="mt-1.5 block size-1.5 shrink-0 rounded-full bg-primary" />
                <span className="text-sm text-foreground/85">
                  Full confirmation sent once deposit is processed
                </span>
              </li>
            </ul>
          </div>

          <p className="text-center text-[11px] text-muted-foreground">
            Split payment terms may vary based on the package and availability.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
